/**
 * Web Mercator quadtree: the canvas grid.
 *
 * A canvas pixel is one Web Mercator pixel at TILE_ZOOM, and a tile is one
 * Web Mercator tile at that zoom: 256 x 256 pixels. Because the grid is defined
 * in the projection the map is drawn in, every pixel is an exact axis-aligned
 * square on screen at every location. A grid defined on the sphere (S2, H3)
 * cannot be, because cube-face or hex axes do not follow meridians.
 *
 * The price is that a pixel is a constant patch of *screen*, not of ground:
 * 1.19 m at the equator, 0.75 m in London, 0.41 m at 70 degrees. Ink is
 * charged per ground area (see tileAreaFactor) so painting a given patch of
 * the world costs the same everywhere.
 */

export const EARTH_RADIUS_M = 6371008.8;
/** Circumference used by Web Mercator (WGS84 semi-major axis). */
const MERCATOR_CIRCUMFERENCE_M = 40075016.685578488;
/** Ground metres per pixel at the equator, at zoom 0 with 256 px tiles. */
export const EQUATOR_METRES_PER_PIXEL_Z0 = MERCATOR_CIRCUMFERENCE_M / 256;

/** Zoom of the canvas tile grid. A pixel here is 1.194 m at the equator. */
export const TILE_ZOOM = 17;
/** Zoom of the activity cell grid, four tiles across (~1.2 km at the equator). */
export const ACTIVITY_ZOOM = 15;
export const TILE_SIZE = 256;
export const TILE_PIXELS = TILE_SIZE * TILE_SIZE;
/** Tiles across the world at TILE_ZOOM. */
export const WORLD_TILES = 1 << TILE_ZOOM;
/** Pixels across the world at TILE_ZOOM. */
export const WORLD_PIXELS = WORLD_TILES * TILE_SIZE;
/** Web Mercator cannot represent the poles; this is the usual cut-off. */
export const MAX_LATITUDE = 85.0511287798066;

const DEG = Math.PI / 180;

export function clampLatitude(lat: number): number {
  return Math.max(-MAX_LATITUDE, Math.min(MAX_LATITUDE, lat));
}

/** Ground metres per canvas pixel at a latitude. */
export function metresPerPixel(lat: number): number {
  return (EQUATOR_METRES_PER_PIXEL_Z0 * Math.cos(clampLatitude(lat) * DEG)) / WORLD_TILES;
}

// ---------------------------------------------------------------------------
// Projection
// ---------------------------------------------------------------------------

/** Global pixel coordinates at TILE_ZOOM. Fractional; floor for a pixel index. */
export interface GlobalPixel {
  gx: number;
  gy: number;
}

export function project(lat: number, lng: number): GlobalPixel {
  const s = Math.sin(clampLatitude(lat) * DEG);
  return {
    gx: ((lng + 180) / 360) * WORLD_PIXELS,
    gy: (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * WORLD_PIXELS,
  };
}

export function unproject(gx: number, gy: number): { lat: number; lng: number } {
  const lng = (gx / WORLD_PIXELS) * 360 - 180;
  const n = Math.PI * (1 - (2 * gy) / WORLD_PIXELS);
  return { lat: Math.atan(Math.sinh(n)) / DEG, lng };
}

/** Wrap a global pixel x into [0, WORLD_PIXELS) so the antimeridian is seamless. */
export function wrapX(gx: number): number {
  return ((gx % WORLD_PIXELS) + WORLD_PIXELS) % WORLD_PIXELS;
}

// ---------------------------------------------------------------------------
// Tokens
// ---------------------------------------------------------------------------

/**
 * Tokens are lower-case hex of the packed grid position. Activity cells carry
 * a "z" prefix: not a hex digit, so a cell token can never parse as a tile.
 */
export const CELL_PREFIX = 'z';
function packToken(x: number, y: number, zoom: number, prefix: string): string {
  return prefix + (y * (1 << zoom) + x).toString(16);
}

function unpackToken(token: string, zoom: number, prefix: string): { x: number; y: number } {
  if (!token.startsWith(prefix)) throw new RangeError(`not a ${prefix || 'tile'} token: ${token}`);
  const body = token.slice(prefix.length);
  if (!/^[0-9a-f]{1,10}$/.test(body)) throw new RangeError(`not a token: ${token}`);
  const packed = parseInt(body, 16);
  const span = 1 << zoom;
  if (!Number.isSafeInteger(packed) || packed < 0 || packed >= span * span) throw new RangeError(`token out of range: ${token}`);
  return { x: packed % span, y: Math.floor(packed / span) };
}

/** Valid tile or activity-cell token. Pass a zoom to require one of the two. */
export function isValidToken(token: string, zoom?: number): boolean {
  try {
    const isCell = token.startsWith(CELL_PREFIX);
    if (zoom !== undefined && zoom !== (isCell ? ACTIVITY_ZOOM : TILE_ZOOM)) return false;
    unpackToken(token, isCell ? ACTIVITY_ZOOM : TILE_ZOOM, isCell ? CELL_PREFIX : '');
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Geodesy (unchanged from the S2 build: plain haversine helpers)
// ---------------------------------------------------------------------------

export function distanceMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const p1 = lat1 * DEG;
  const p2 = lat2 * DEG;
  const dp = (lat2 - lat1) * DEG;
  const dl = (lng2 - lng1) * DEG;
  const a = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
}

export function offsetLatLng(lat: number, lng: number, eastM: number, northM: number): { lat: number; lng: number } {
  const dLat = (northM / EARTH_RADIUS_M) / DEG;
  const dLng = (eastM / (EARTH_RADIUS_M * Math.cos(lat * DEG))) / DEG;
  return { lat: lat + dLat, lng: lng + dLng };
}

export { packToken, unpackToken };
